import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Clock, Users, Car, Home, Star, Calendar, ChevronLeft, ChevronRight, MessageSquare, Camera, BadgeCheck, Shield, Lock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { experiencesApi, bookingsApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { StarRating } from '@/components/StarRating';
import { toast } from 'sonner';
import SocialShareButtons from '@/components/SocialShareButtons';

// Function to upgrade image quality for detail pages
const upgradeImageQuality = (url) => {
  if (!url) return url;
  // Replace low quality params with high quality
  return url
    .replace(/w=800/g, 'w=1600')
    .replace(/w=400/g, 'w=1600')
    .replace(/q=80/g, 'q=90')
    .replace(/q=70/g, 'q=90');
};

const ExperienceDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [communityPhotos, setCommunityPhotos] = useState([]);
  
  // Booking form
  // Default date to tomorrow
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  
  const [selectedDate, setSelectedDate] = useState(getTomorrow());
  const [participants, setParticipants] = useState(1);
  const [includeVehicle, setIncludeVehicle] = useState(false);
  const [includeAccommodation, setIncludeAccommodation] = useState(false);
  const [message, setMessage] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await experiencesApi.getById(id);
        setData(result);
        
        // Fetch community photos - use current origin in production
        const getApiUrl = () => {
          if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
            return window.location.origin;
          }
          return process.env.REACT_APP_BACKEND_URL || window.location.origin;
        };
        const API_URL = getApiUrl();
        const photosResponse = await fetch(`${API_URL}/api/experiences/${id}/community-photos`);
        if (photosResponse.ok) {
          const photosData = await photosResponse.json();
          setCommunityPhotos(photosData.photos || []);
        }
      } catch (error) {
        console.error('Error fetching experience:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, t]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-muted-foreground">Expérience non trouvée</p>
      </div>
    );
  }

  const { experience, explorer, reviews } = data;
  const title = i18n.language === 'en' && experience.title_en ? experience.title_en : experience.title;
  const description = i18n.language === 'en' && experience.description_en ? experience.description_en : experience.description;
  
  // Determine if this is an event (festival, market, etc.) vs a guided experience (workshop, tour)
  const categories = experience.categories || [experience.category];
  const isLocalEvent = categories.some(cat => cat === 'local_events');
  const isRetreat = categories.some(cat => cat === 'healing');
  const isSharedEvent = isLocalEvent; // Events like festivals, markets are "shared" - not linked to a specific guide
  
  // Check if this is a FREE experience (price = 0 or meetup type) that requires membership
  const isFreeExperience = experience.price === 0 || experience.experience_type === 'meetup';
  const isEvent = experience.experience_type === 'event'; // Events are free and open to everyone
  const requiresMembership = isFreeExperience && !isEvent; // Free experiences (not events) require membership
  
  // Check if user has active membership
  const userIsMember = user?.subscription_active || user?.is_member;
  const canAccessFreeContent = !requiresMembership || userIsMember || !isAuthenticated; // Non-logged users see teaser
  
  // For non-members viewing free content, we'll blur/hide certain details
  const showLockedContent = requiresMembership && isAuthenticated && !userIsMember;
  
  // Default images based on category/location
  const getDefaultImage = () => {
    const category = categories[0] || experience.category || '';
    const location = (experience.location || '').toLowerCase();
    
    // Location-based defaults
    if (location.includes('paris')) {
      return 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=90'; // Eiffel Tower
    }
    if (location.includes('alpes') || location.includes('montagne')) {
      return 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=90'; // Mountains
    }
    if (location.includes('mer') || location.includes('plage') || location.includes('côte')) {
      return 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=90'; // Beach
    }
    
    // Category-based defaults
    const categoryDefaults = {
      'cultural_tours': 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1600&q=90', // Paris street
      'adventure': 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=1600&q=90', // Hiking
      'nature': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1600&q=90', // Forest
      'gastronomy': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=90', // Food
      'wellness': 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=1600&q=90', // Yoga
      'healing': 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=1600&q=90', // Yoga
      'artisan': 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=1600&q=90', // Craft
      'local_events': 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1600&q=90', // Festival
      'fun': 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=1600&q=90', // Friends
    };
    
    return categoryDefaults[category] || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600&q=90'; // Nature landscape default
  };
  
  const images = experience.images?.length > 0 
    ? experience.images.map(img => upgradeImageQuality(img))
    : [getDefaultImage()];

  const calculateTotal = () => {
    let total = experience.price * participants;
    if (includeVehicle && experience.vehicle_price) total += experience.vehicle_price;
    if (includeAccommodation && experience.accommodation_price) total += experience.accommodation_price;
    return total;
  };

  const handleBooking = async () => {
    // Vérification authentification
    if (!isAuthenticated) {
      toast.info(i18n.language === 'fr' ? 'Veuillez vous connecter pour réserver' : 'Please log in to book');
      navigate('/login', { state: { from: { pathname: `/experience/${id}` } } });
      return;
    }

    // Vérification date
    if (!selectedDate) {
      toast.error(i18n.language === 'fr' ? 'Veuillez sélectionner une date' : 'Please select a date');
      return;
    }

    // Vérification rôle
    if (user.role !== 'experimenter') {
      toast.error(i18n.language === 'fr' ? 'Seuls les voyageurs peuvent réserver' : 'Only travelers can book');
      return;
    }

    // Vérification abonnement pour expériences gratuites
    if (requiresMembership && !userIsMember) {
      toast.error(i18n.language === 'fr' 
        ? 'Un abonnement membre (49€/an) est requis pour cette expérience gratuite'
        : 'A membership (€49/year) is required for this free experience');
      navigate('/membership');
      return;
    }

    setBookingLoading(true);
    try {
      const booking = await bookingsApi.create({
        experience_id: id,
        date: selectedDate,
        participants,
        include_vehicle: includeVehicle,
        include_accommodation: includeAccommodation,
        message
      });
      
      toast.success(i18n.language === 'fr' ? 'Réservation créée avec succès !' : 'Booking created successfully!');
      navigate(`/booking/${booking.booking_id}`);
    } catch (error) {
      console.error('Booking error:', error);
      const errorMsg = error.response?.data?.detail || (i18n.language === 'fr' ? 'Erreur lors de la réservation' : 'Booking error');
      toast.error(errorMsg);
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-20 pb-12" data-testid="experience-detail-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Image Gallery */}
        <div className="relative rounded-3xl overflow-hidden mb-8 aspect-[16/9] md:aspect-[21/9]">
          <img 
            src={images[currentImageIndex]}
            alt={title}
            className="w-full h-full object-cover"
          />
          
          {images.length > 1 && (
            <>
              <button
                onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                data-testid="prev-image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                data-testid="next-image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Info */}
            <div>
              {/* Event/Experience Type Badge */}
              {(isLocalEvent || isRetreat) && (
                <div className="flex items-center gap-2 mb-3">
                  {experience.is_featured && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-bold rounded-full">
                      <Star className="w-4 h-4" fill="white" />
                      {i18n.language === 'fr' ? 'À la une' : 'Featured'}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${
                    isLocalEvent 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {isLocalEvent 
                      ? (i18n.language === 'fr' ? 'Événement local' : 'Local Event')
                      : (i18n.language === 'fr' ? 'Retraite bien-être' : 'Wellness Retreat')
                    }
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <MapPin className="w-4 h-4" />
                <span>{experience.location}</span>
                {experience.rating > 0 && (
                  <>
                    <span>•</span>
                    <StarRating rating={experience.rating} size="sm" />
                    <span className="text-sm">({experience.review_count} {t('exp.reviews')})</span>
                  </>
                )}
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{title}</h1>
              
              {/* Share Button */}
              <div className="mb-4">
                <SocialShareButtons 
                  url={window.location.href}
                  title={title}
                  description={description}
                />
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 px-3 py-2 bg-secondary rounded-full">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>{experience.duration_hours} {t('exp.hours')}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-secondary rounded-full">
                  <Users className="w-4 h-4 text-primary" />
                  <span>{experience.max_participants} {t('exp.participants')}</span>
                </div>
                {experience.includes_vehicle && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-full">
                    <Car className="w-4 h-4" />
                    <span>{t('exp.includes_vehicle')}</span>
                  </div>
                )}
                {experience.includes_accommodation && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-accent/10 text-accent rounded-full">
                    <Home className="w-4 h-4" />
                    <span>{t('exp.includes_accommodation')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Membership Required Banner for Free Experiences */}
            {showLockedContent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-purple-100 to-purple-50 border-2 border-purple-300 rounded-2xl p-6"
                data-testid="membership-required-banner"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-purple-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-purple-900 mb-2">
                      {i18n.language === 'fr' ? 'Expérience réservée aux membres' : 'Members-only experience'}
                    </h3>
                    <p className="text-purple-800 mb-4">
                      {i18n.language === 'fr' 
                        ? 'Cette expérience gratuite est réservée aux membres XPerience. Devenez membre pour seulement 49€/an et accédez à toutes les expériences gratuites proposées par nos guides passionnés.'
                        : 'This free experience is reserved for XPerience members. Become a member for only €49/year and access all free experiences offered by our passionate guides.'}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={() => navigate('/membership')}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="become-member-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {i18n.language === 'fr' ? 'Devenir membre (49€/an)' : 'Become a member (€49/year)'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => navigate('/explore')}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        {i18n.language === 'fr' ? 'Voir les expériences payantes' : 'See paid experiences'}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Description */}
            <div className={`prose prose-lg max-w-none ${showLockedContent ? 'relative' : ''}`}>
              {showLockedContent && (
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white z-10 pointer-events-none" />
              )}
              <p className={`text-foreground/80 leading-relaxed whitespace-pre-line ${showLockedContent ? 'line-clamp-3' : ''}`}>
                {description}
              </p>
            </div>

            {/* Explorer - Only show for guided experiences, not for shared events */}
            {explorer && !isSharedEvent && (
              <div className={`border rounded-2xl p-6 ${showLockedContent ? 'relative overflow-hidden' : ''}`}>
                {showLockedContent && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex items-center justify-center">
                    <div className="text-center">
                      <Lock className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                      <p className="text-sm text-purple-700 font-medium">
                        {i18n.language === 'fr' ? 'Réservé aux membres' : 'Members only'}
                      </p>
                    </div>
                  </div>
                )}
                <h3 className="font-semibold mb-4">{t('exp.about_explorer')}</h3>
                <div className="flex items-start gap-4">
                  <img 
                    src={showLockedContent ? 'https://ui-avatars.com/api/?name=?&background=ddd&color=999&size=80' : (explorer.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(explorer.name)}&background=2C5F2D&color=fff&size=80`)}
                    alt={explorer.name}
                    className={`w-16 h-16 rounded-full object-cover ${showLockedContent ? 'blur-sm' : ''}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-semibold text-lg ${showLockedContent ? 'blur-sm' : ''}`}>
                        {showLockedContent ? '••••••••' : explorer.name}
                      </h4>
                      {explorer.verified && !showLockedContent && (
                        <BadgeCheck className="w-5 h-5 text-blue-500" title={i18n.language === 'fr' ? 'Guide vérifié' : 'Verified guide'} />
                      )}
                    </div>
                    {explorer.location && !showLockedContent && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {explorer.location}
                      </p>
                    )}
                    {explorer.bio && !showLockedContent && (
                      <p className="text-muted-foreground mt-2">{explorer.bio}</p>
                    )}
                    {explorer.rating > 0 && !showLockedContent && (
                      <div className="mt-2">
                        <StarRating rating={explorer.rating} size="sm" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* For shared events, show organizer info instead */}
            {isSharedEvent && experience.organizer_name && (
              <div className="border rounded-2xl p-6 bg-purple-50 border-purple-200">
                <h3 className="font-semibold mb-4 text-purple-900">
                  {i18n.language === 'fr' ? 'Organisateur' : 'Organizer'}
                </h3>
                <div className="flex items-center gap-3">
                  {experience.organizer_picture ? (
                    <img 
                      src={experience.organizer_picture}
                      alt={experience.organizer_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center">
                      <span className="text-purple-700 font-semibold text-lg">
                        {experience.organizer_name?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-purple-900">{experience.organizer_name}</p>
                    <p className="text-sm text-purple-600">
                      {i18n.language === 'fr' ? 'Organisateur d\'événement' : 'Event organizer'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Cancellation Policy - Uniform XPerience policy */}
            {!isSharedEvent && (
              <div className="border-2 border-primary/30 rounded-xl p-4 bg-orange-50">
                <h4 className="font-semibold mb-2 text-primary flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  {i18n.language === 'fr' ? 'Politique d\'annulation XPerience' : 'XPerience Cancellation Policy'}
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    {i18n.language === 'fr' 
                      ? 'Plus de 24h avant : Remboursement 100%'
                      : 'More than 24h before: 100% refund'}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    {i18n.language === 'fr' 
                      ? 'Moins de 24h avant : Pas de remboursement'
                      : 'Less than 24h before: No refund'}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    {i18n.language === 'fr' 
                      ? 'Météo / Force majeure : Remboursement ou report'
                      : 'Weather / Force majeure: Refund or reschedule'}
                  </li>
                </ul>
              </div>
            )}

            {/* Reviews */}
            {reviews && reviews.length > 0 && (
              <div>
                <h3 className="font-semibold text-xl mb-4">{t('exp.reviews')} ({reviews.length})</h3>
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="border rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <img 
                          src={review.experimenter_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.experimenter_name)}&size=40`}
                          alt={review.experimenter_name}
                          className="w-10 h-10 rounded-full"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h5 className="font-medium">{review.experimenter_name}</h5>
                            <StarRating rating={review.rating} size="sm" showValue={false} />
                          </div>
                          <p className="text-muted-foreground mt-1">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Community Photos */}
            {communityPhotos && communityPhotos.length > 0 && (
              <div className="border-t pt-8">
                <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-purple-600" />
                  {i18n.language === 'fr' ? 'Photos de la communauté' : 'Community Photos'}
                  <span className="text-sm font-normal text-muted-foreground">({communityPhotos.length})</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {communityPhotos.map((photo) => (
                    <div key={photo.photo_id} className="space-y-2">
                      {photo.photo_urls?.map((url, idx) => (
                        <div key={idx} className="group relative rounded-xl overflow-hidden aspect-square">
                          <img
                            src={url}
                            alt={photo.caption || 'Community photo'}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute bottom-3 left-3 right-3">
                              <p className="text-white text-sm font-medium">{photo.user_name}</p>
                              {photo.caption && (
                                <p className="text-white/80 text-xs mt-1 line-clamp-2">{photo.caption}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="booking-summary" data-testid="booking-form">
              {/* Price display based on type */}
              {experience.experience_type === 'meetup' ? (
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-bold text-purple-600 line-through opacity-50">0€</span>
                    <span className="text-3xl font-bold text-purple-600">5€</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {i18n.language === 'fr' ? 'Rencontre gratuite • Frais plateforme 5€' : 'Free meetup • Platform fee €5'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {i18n.language === 'fr' 
                      ? 'Le guide partage sa passion gratuitement'
                      : 'The guide shares their passion for free'}
                  </p>
                </div>
              ) : experience.experience_type === 'event' ? (
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold text-amber-600">{i18n.language === 'fr' ? 'Gratuit' : 'Free'}</span>
                  <p className="text-sm text-muted-foreground mt-2">
                    {i18n.language === 'fr' ? 'Événement local' : 'Local event'}
                  </p>
                </div>
              ) : (
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-bold text-primary">{experience.price}€</span>
                  <span className="text-muted-foreground">{t('exp.per_person')}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('exp.select_date')}</label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="date-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('exp.select_participants')}</label>
                  <Input
                    type="number"
                    value={participants}
                    onChange={(e) => setParticipants(Math.max(1, Math.min(experience.max_participants, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={experience.max_participants}
                    data-testid="participants-input"
                  />
                </div>

                {experience.vehicle_price > 0 && experience.experience_type === 'experience' && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="vehicle"
                        checked={includeVehicle}
                        onCheckedChange={setIncludeVehicle}
                        data-testid="vehicle-checkbox"
                      />
                      <label htmlFor="vehicle" className="text-sm cursor-pointer">
                        {t('exp.add_vehicle')}
                      </label>
                    </div>
                    <span className="text-sm font-medium">+{experience.vehicle_price}€</span>
                  </div>
                )}

                {experience.accommodation_price > 0 && experience.experience_type === 'experience' && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="accommodation"
                        checked={includeAccommodation}
                        onCheckedChange={setIncludeAccommodation}
                        data-testid="accommodation-checkbox"
                      />
                      <label htmlFor="accommodation" className="text-sm cursor-pointer">
                        {t('exp.add_accommodation')}
                      </label>
                    </div>
                    <span className="text-sm font-medium">+{experience.accommodation_price}€</span>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('exp.message')}</label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="..."
                    rows={3}
                    data-testid="message-input"
                  />
                </div>

                {/* Total for paid experiences */}
                {experience.experience_type === 'experience' && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center text-lg font-semibold">
                      <span>{t('exp.total')}</span>
                      <span className="text-primary">{calculateTotal()}€</span>
                    </div>
                  </div>
                )}

                {/* Booking button */}
                <Button
                  onClick={handleBooking}
                  disabled={bookingLoading}
                  className={`w-full py-6 text-lg ${
                    experience.experience_type === 'meetup' 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : experience.experience_type === 'event'
                        ? 'bg-amber-600 hover:bg-amber-700 text-white'
                        : 'bg-accent hover:bg-accent/90 text-accent-foreground btn-accent-glow'
                  }`}
                  data-testid="book-btn"
                >
                  {bookingLoading 
                    ? t('common.loading') 
                    : experience.experience_type === 'meetup' || experience.experience_type === 'event'
                      ? (i18n.language === 'fr' ? 'Participer' : 'Join')
                      : t('exp.book_now')
                  }
                </Button>

                {/* Support XPerience button for free types */}
                {(experience.experience_type === 'meetup') && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                    <p className="text-sm text-gray-700 mb-3 text-center">
                      {i18n.language === 'fr' 
                        ? 'Cette rencontre est gratuite. Si vous appréciez la plateforme, vous pouvez nous soutenir.'
                        : 'This meetup is free. If you appreciate the platform, you can support us.'}
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary hover:text-white"
                      onClick={() => window.open('https://xperience-guides.com/donate', '_blank')}
                    >
                      ❤️ {i18n.language === 'fr' ? 'Soutenir XPerience' : 'Support XPerience'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ExperienceDetailPage;
